import { get, isEmpty } from 'lodash';
import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { PREVIEW_TAG } from '@nova/core';

/**
 * @typedef {Object} AutocompleteOptions
 * @property {string} [search]
 * @property {number} [page]
 * @property {number} [limit]
 * @property {string} [country] - ISO-3166 alpha-3 country code to filter results by.
 * @property {boolean} [verified] - When set, filters results by verification status.
 */

/**
 * @typedef {Object} AutocompleteItem
 * @property {string} id - The unique identifier of the company.
 * @property {boolean} isActive - Indicates whether the company is active.
 * @property {string} name - The name of the company.
 * @property {string|null} scac - The SCAC code of the company.
 * @property {string|null} mc - The MC number of the company.
 * @property {string|null} usdot - The USDOT number of the company.
 * @property {string|null} type - The type of the company.
 * @property {string|null} country - ISO-3166 alpha-3 country code.
 * @property {string|null} street - Company street address.
 * @property {string|null} city - Company city.
 * @property {string|null} state - Company state/province.
 * @property {string|null} legalName - Legal company name.
 * @property {string|null} zipCode - Postal code.
 * @property {string|null} phoneNumber - Company phone number.
 * @property {Date|null} verifiedAt - Verification timestamp.
 * @property {import('@nova/core').CompanyVerifiedBy|null} verifiedBy - The entity that verified the company.
 * @property {string[]} identifiers - Active company identifiers.
 */

/**
 * @typedef {Object} AutocompleteResult
 * @property {AutocompleteItem[]} items - The companies which matched the search.
 * @property {number} count - The number of items in the current page
 * @property {number} page - The current page number.
 * @property {number} total - The total number of companies which match the search.
 * @property {number} totalPages - The total number of pages available.
 */

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

const service = {
  async getCompanyById(companyId, params = {}, options = {}, requestConfig = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(
      `company/${companyId}?${queryString}`,
      {
        params
      },
      requestConfig
    );
    return getResponse(response, options);
  },
  async getCompanies(params, options = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(`company?${queryString}`, { params });
    return getResponse(response, options);
  },
  /**
   * Create a carrier company. Blank or whitespace-only `scac` is omitted without
   * mutating `data`; a surviving `scac` is stored trimmed.
   *
   * @template TResponse
   * @param {Record<string, unknown>} data - Company create payload.
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options] - Response handling options.
   * @returns {Promise<TResponse>}
   */
  async createCompany(data, options = {}) {
    const payload = { ...data };
    // `isEmpty` is length-based, so "   " would survive it and pass both the client
    // `optionalLengthBetween(2, 4)` rule and Neutron's `@Length(2, 4)`.
    const scac = typeof payload.scac === 'string' ? payload.scac.trim() : payload.scac;
    if (isEmpty(scac)) {
      delete payload.scac;
    } else {
      payload.scac = scac;
    }

    const response = await axios.post('company', payload);
    return getResponse(response, options);
  },
  async getPreviewCompany() {
    const companies = await this.getCompanies({
      s: {
        tags: {
          $cont: PREVIEW_TAG
        }
      }
    });
    return companies?.[0] ?? null;
  },
  /**
   * Get autocompletion for companies.
   * @param {AutocompleteOptions} [options={}] - The optional parameters for autocompletion.
   * @param {import('axios').AxiosRequestConfig} [requestConfig={}] - The optional Axios request configuration.
   * @returns {Promise<AutocompleteResult>}
   */
  async autocomplete(options = {}, requestConfig = {}) {
    const defaultLimit = 15;
    const { limit, page, search, country, verified } = {
      limit: defaultLimit,
      page: 1,
      search: '',
      ...options
    };

    const offset = (page - 1) * limit;
    const payload = {
      search,
      pagination: { limit, offset },
      ...(country ? { country } : {}),
      ...(verified === undefined ? {} : { verified })
    };
    try {
      const response = await axios.post('/company-search/autocomplete', payload, requestConfig);
      const items = get(response, 'data.items', []);
      const total = get(response, 'data.meta.totalRecords', 0);
      const totalPages = get(response, 'data.meta.totalPages', 0);
      const count = get(items, 'length', 0);
      return { items, count, page, total, totalPages };
    } catch (err) {
      return { items: [], count: 0, page, total: 0, totalPages: 0 };
    }
  }
};

export default service;
